import React, { useState, useCallback, useMemo } from 'react';
import { ModelDataProvider } from '@mes-amis/super-form';
import type { ModelRecord, QueryModelOptions, SuperFormDefinition } from '@mes-amis/super-form';
import { getContactsForClient, saveContact, syncContact } from '@/db/services/contact_service';
import type { Contact } from '@/db/types/contact';
import { isOnline } from '@/lib/connectivity';
import { contactRelationshipOptions } from '@/lib/contact-relationship-options';
import CreateContactModal from './CreateContactModal';

interface ModalState {
  clientId: number;
  existingContact?: Contact;
  resolve: (record: ModelRecord) => void;
  reject: (error: Error) => void;
}

interface ContactModelDataProviderProps {
  children: React.ReactNode;
  /** Template config the contact modal reads its relationship choices from */
  definition?: SuperFormDefinition;
}

/**
 * Wraps children with ModelDataProvider configured for Contact model.
 *
 * - queryRecords: reads contacts from IndexedDB, filters by scope/ids
 * - createRecord: opens CreateContactModal and resolves when the contact is saved
 */
const ContactModelDataProvider: React.FC<ContactModelDataProviderProps> = ({ children, definition }) => {
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const relationshipOptions = useMemo(() => contactRelationshipOptions(definition), [definition]);

  const queryRecords = useCallback(async (model: string, options: QueryModelOptions): Promise<ModelRecord[]> => {
    if (model !== 'Contact') return [];

    const clientId = options.clientId;
    if (!clientId) return [];

    let contacts = await getContactsForClient(clientId);

    // Apply scope filters (e.g. { emergency: true })
    if (options.scope) {
      contacts = contacts.filter(contact => {
        for (const [key, val] of Object.entries(options.scope!)) {
          if ((contact as unknown as Record<string, unknown>)[key] !== val) return false;
        }
        return true;
      });
    }

    // Apply ID filters
    if (options.ids && options.ids.length > 0) {
      contacts = contacts.filter(c => options.ids!.includes(c.id));
    }

    return contacts.map(c => ({
      id: c.id,
      label: formatContactLabel(c),
      data: c,
    }));
  }, []);

  const createRecord = useCallback((model: string, _data: unknown, clientId: number): Promise<ModelRecord> => {
    if (model !== 'Contact') {
      return Promise.reject(new Error(`Unsupported model: ${model}`));
    }

    return new Promise<ModelRecord>((resolve, reject) => {
      setModalState({ clientId, resolve, reject });
    });
  }, []);

  const editRecord = useCallback((model: string, record: ModelRecord, clientId: number): Promise<ModelRecord> => {
    if (model !== 'Contact') {
      return Promise.reject(new Error(`Unsupported model: ${model}`));
    }

    return new Promise<ModelRecord>((resolve, reject) => {
      setModalState({ clientId, existingContact: record.data as Contact, resolve, reject });
    });
  }, []);

  const handleSave = useCallback(async (contactData: Partial<Contact>) => {
    if (!modalState) return;

    const now = new Date().toISOString();
    const existing = modalState.existingContact;

    // For new contacts, generate a local UUID id.
    // For edits of server-synced contacts (existing.synced was true), clear local_id
    // so syncContact sends a PATCH (update) instead of POST (create).
    const isNewContact = !existing;
    const wasServerSynced = existing?.synced === true;

    const contact: Contact = {
      id: existing?.id ?? `contact-${crypto.randomUUID()}`,
      local_id: isNewContact ? `contact-${crypto.randomUUID()}` : (wasServerSynced ? undefined : existing?.local_id),
      customer_id: contactData.customer_id!,
      relationship: contactData.relationship ?? null,
      note: contactData.note ?? null,
      designated: contactData.designated ?? false,
      emergency: contactData.emergency ?? false,
      legal_representative: contactData.legal_representative ?? false,
      physician: contactData.physician ?? false,
      caregiver: contactData.caregiver ?? false,
      person: contactData.person as Contact['person'],
      address: contactData.address as Contact['address'],
      synced: false,
      created_at: existing?.created_at ?? now,
      updated_at: now,
    } as Contact;

    await saveContact(contact);

    // If online, sync immediately so the server has the latest data.
    // On failure we just log — the contact is already saved locally with
    // synced: false and will be picked up by the next document sync.
    if (isOnline()) {
      try {
        const synced = await syncContact(contact);
        if (synced) {
          Object.assign(contact, synced);
        }
      } catch (err) {
        console.warn('Contact sync after edit failed (will retry on next document save):', err);
      }
    }

    const record: ModelRecord = {
      id: contact.id,
      label: formatContactLabel(contact),
      data: contact,
    };

    modalState.resolve(record);
    setModalState(null);
  }, [modalState]);

  const handleClose = useCallback(() => {
    // Reject so ModelSelectField's catch block handles cancellation gracefully
    modalState?.reject(new Error('cancelled'));
    setModalState(null);
  }, [modalState]);

  return (
    <ModelDataProvider queryRecords={queryRecords} createRecord={createRecord} editRecord={editRecord}>
      {children}
      <CreateContactModal
        isOpen={!!modalState}
        onClose={handleClose}
        onSave={handleSave}
        clientId={modalState?.clientId ?? 0}
        relationshipOptions={relationshipOptions}
        initialContact={modalState?.existingContact}
      />
    </ModelDataProvider>
  );
};

function formatContactLabel(contact: Contact): string {
  const firstName = contact.person?.first_name || '';
  const lastName = contact.person?.last_name || '';
  const name = `${firstName} ${lastName}`.trim();
  return name || `Contact #${contact.id}`;
}

export default ContactModelDataProvider;
